import React from 'react';
import PropTypes from 'prop-types';
import { Transition as ReactTransition } from 'react-transition-group';
import Velocity from 'velocity-animate';

import Browser from '@wetransfer/concorde-browser';

import Analytics from '@module/analytics';
import I18n from '@module/i18n';
import LaunchDarkly from '@module/launchdarkly';

import FeatureFlagsHelper from '@app/helper/featureflags';
import Store from '@app/store';

import Icon from '@component/icon';

import {
  EXPERIMENT_USA_UNLIMITED_OFFER,
  EXPERIMENT_USA_UNLIMITED_OFFER_VARIANT_CONTROL,
  EXPERIMENT_USA_UNLIMITED_OFFER_VARIANT_UNLIMITED_ONLY,
  EXPERIMENT_USA_UNLIMITED_OFFER_VARIANT_UNLIMITED_PLUS,
} from '@constant/experiments';

import { WHITE, SUN, GOLDEN_ROD } from '@constant/colors';

const variants = {
  gold: {
    className: 'splashpage__gold',
    trackingCode: 'Splashpage_v40_yellow',
    leftBg: 'splashpage__background-leftGold',
    rightBg: 'splashpage__background-rightGold',
    plusIconColor: WHITE,
    plusIconBackgroundColor: SUN,
  },
  purple: {
    className: 'splashpage__purple',
    trackingCode: 'Splashpage_v40_purple',
    leftBg: 'splashpage__background-leftPurple',
    rightBg: 'splashpage__background-rightPurple',
    plusIconColor: '#3C2B3A',
    plusIconBackgroundColor: GOLDEN_ROD,
  },
};

export class Control extends React.Component {
  constructor(props) {
    super(props);

    this.variant = this.getVariant();
  }

  handleEscape = (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    Analytics.trackStructEvent({
      category: 'splashscreen',
      action: 'click_take_me_to_free',
    });

    this.props.onEscape();
  };

  // Fade-out and scale up when the component is going away
  onExit(component) {
    Velocity(
      component,
      {
        scale: '1.3',
        opacity: 0,
      },
      { duration: 300 }
    );
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleEscape);

    Analytics.trackStructEvent({
      category: 'splashscreen',
      action: 'show_page',
    });
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleEscape);
  }

  //Logic to determine which Splashpage variant load
  randomVariant() {
    return Math.floor(Math.random() * 10) % 2 === 0 ? 'gold' : 'purple';
  }

  //Sets all the var needed to display the page based on the randomVariant
  getVariant() {
    return variants[this.randomVariant()];
  }

  handleClick = () => {
    Analytics.trackStructEvent({
      category: 'splashscreen',
      action: 'click_take_me_to_free',
    });
    this.props.onClick();
  };

  handleFreeClick = (event) => {
    // Stop Triggering GA tracker in handleClick()
    event.stopPropagation();

    Analytics.trackStructEvent({
      category: 'splashscreen',
      action: 'click_take_me_to_free',
    });
    this.props.onFreeClick();
  };

  handlePlusClick = (event) => {
    // Stop Triggering GA tracker in handleClick()
    event.stopPropagation();

    Analytics.trackStructEvent({
      category: 'splashscreen',
      action: 'click_get_wetransfer_plus',
    });
    this.props.onPlusClick(this.variant.trackingCode);
  };

  makeFeatureList(type = 'free') {
    const countryFromState = Store.getState().session.region.country;
    const USAUnlimitedExperimentUnlimitedOnly =
      !Browser.isMobile &&
      countryFromState === 'US' &&
      LaunchDarkly.getVariant(
        EXPERIMENT_USA_UNLIMITED_OFFER,
        EXPERIMENT_USA_UNLIMITED_OFFER_VARIANT_CONTROL
      ) === EXPERIMENT_USA_UNLIMITED_OFFER_VARIANT_UNLIMITED_ONLY;

    const USAUnlimitedExperimentUnlimitedPlus =
      !Browser.isMobile &&
      countryFromState === 'US' &&
      LaunchDarkly.getVariant(
        EXPERIMENT_USA_UNLIMITED_OFFER,
        EXPERIMENT_USA_UNLIMITED_OFFER_VARIANT_CONTROL
      ) === EXPERIMENT_USA_UNLIMITED_OFFER_VARIANT_UNLIMITED_PLUS;

    if (type === 'free') {
      if (USAUnlimitedExperimentUnlimitedOnly) {
        return (
          <ul className="plan__features">
            <li>Send up to 2GB</li>
            <li className="plan__features--is-faded">Store 500GB of files</li>
            <li className="plan__features--is-faded">
              Keep transfers for longer
            </li>
            <li className="plan__features--is-faded">Protect your transfers</li>
          </ul>
        );
      }
      if (USAUnlimitedExperimentUnlimitedPlus) {
        return (
          <ul className="plan__features">
            <li>Send up to 2GB</li>
            <li className="plan__features--is-faded">Store up to 500GB</li>
            <li className="plan__features--is-faded">
              Keep transfers for longer
            </li>
            <li className="plan__features--is-faded">Protect your transfers</li>
          </ul>
        );
      }
    } else {
      if (USAUnlimitedExperimentUnlimitedOnly) {
        return (
          <ul className="plan__features">
            <li>Send files of any size</li>
            <li>Store 500GB of files</li>
            <li>Keep transfers for longer</li>
            <li>Protect your transfers</li>
          </ul>
        );
      }
      if (USAUnlimitedExperimentUnlimitedPlus) {
        return (
          <ul className="plan__features">
            <li>Options for any file size</li>
            <li>Store up to 500GB</li>
            <li>Keep transfers for longer</li>
            <li>Protect your transfers</li>
          </ul>
        );
      }
    }

    let moreFeatures;
    if (type === 'plus' && FeatureFlagsHelper.unlimitedTransfers) {
      moreFeatures = <li>{I18n.t('splash.plan.plus.more_features')}</li>;
    }

    let featureList = [];
    if (type === 'free') {
      featureList = [
        'splash.plan.free.feature_1',
        'splash.plan.plus.feature_2',
        'splash.plan.plus.feature_3',
        'splash.plan.plus.feature_4',
      ];
    } else {
      featureList = [
        'splash.plan.plus.feature_1',
        'splash.plan.plus.feature_2',
        'splash.plan.plus.feature_3',
        'splash.plan.plus.feature_4',
      ];
    }

    let unlimitedKey = '';
    if (FeatureFlagsHelper.unlimitedTransfers) {
      unlimitedKey = '_unlimited';
      featureList = featureList.concat([
        'splash.plan.plus.feature_5',
        'splash.plan.plus.feature_6',
      ]);
    }

    const features = featureList.map((feature, index) => {
      let featureClassName = '';
      if (type === 'free' && index > 0) {
        featureClassName = 'plan__features--is-faded';
      }
      return (
        <li
          key={`splashpage-feature-${type}-${index}`}
          className={featureClassName}
        >
          {I18n.t(`${feature}${unlimitedKey}`)}
        </li>
      );
    });

    return (
      <React.Fragment>
        {features}
        {moreFeatures}
      </React.Fragment>
    );
  }

  renderContent() {
    const leftClassnames = [
      'splashpage__background-sides',
      this.variant.leftBg,
    ];
    const rightClassnames = [
      'splashpage__background-sides',
      this.variant.rightBg,
    ];

    let headingKeyLineOne = 'splash.heading.line_1';
    let headingKeyLineTwo = 'splash.heading.line_2';
    let leftButtonCopyKey = 'splash.plan.free.cta';
    let rightButtonCopyKey = 'splash.plan.plus.cta';

    if (FeatureFlagsHelper.unlimitedTransfers) {
      headingKeyLineOne = 'splash.heading.line_1_unlimited';
      headingKeyLineTwo = 'splash.heading.line_2_unlimited';
      leftButtonCopyKey = 'splash.plan.free.cta_unlimited';
      rightButtonCopyKey = 'splash.plan.plus.cta_unlimited';
    }

    return (
      <div key="splashpage-2017" className="splash-201708">
        <div className="splashpage__background">
          <div className={leftClassnames.join(' ')} />
          <div className={rightClassnames.join(' ')} />
        </div>

        <div className="content">
          <h1 className="content__heading">
            <span>{I18n.t(headingKeyLineOne)}</span>
            <span
              dangerouslySetInnerHTML={{ __html: I18n.t(headingKeyLineTwo) }}
            />
          </h1>

          <div className="content__plans">
            <div className="plan">
              <h2 className="plan__heading">{I18n.t('site.name')}</h2>
              <ul className="plan__features">{this.makeFeatureList()}</ul>
              <button
                className="button button--left"
                onClick={this.handleFreeClick}
              >
                {I18n.t(leftButtonCopyKey)}
              </button>
            </div>

            <div className="plan">
              <h2 className="plan__heading">
                {I18n.t('site.name')}
                <Icon
                  type="TransferPlus"
                  color={this.variant.plusIconColor}
                  backgroundColor={this.variant.plusIconBackgroundColor}
                />
              </h2>
              <ul className="plan__features">{this.makeFeatureList('plus')}</ul>
              <button
                className="button button--right"
                onClick={this.handlePlusClick}
              >
                {I18n.t(rightButtonCopyKey)}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render our splash page
  render() {
    const classNames = ['splashpage', this.variant.className];

    if (FeatureFlagsHelper.unlimitedTransfers) {
      classNames.push('splashpage--is-new');
    }

    return (
      <ReactTransition
        in={this.props.in}
        timeout={{ in: 0, exit: 300 }}
        unmountOnExit={true}
        onExit={this.onExit}
      >
        <div className={classNames.join(' ')} onClick={this.handleClick}>
          {this.renderContent()}
        </div>
      </ReactTransition>
    );
  }
}

Control.propTypes = {
  in: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  onFreeClick: PropTypes.func.isRequired,
  onPlusClick: PropTypes.func.isRequired,
  onEscape: PropTypes.func.isRequired,
};

export default Control;
